import { BarChartD3 } from './barchart-base.js';

// Company dividends bar chart.
// Small chart on the company dashboard showing dividend totals.
// Supports two modes (driven entirely by server-supplied state):
//   - year mode: fixed bar width (8px), 4-digit full label / 2-digit short label
//   - month mode: fixed bar width with proportional shrink on narrow screens;
//                 X axis shows only year labels ('YY), once per year group
// Differences from DivCalendarD3:
//   - smaller; no click handler
//   - tooltip is the only way to see details for a given bar (X axis can be crowded)
//   - Y axis on the right (per requirements); X axis renders adaptively (thinning + edges)
//   - tooltip is rendered via a server-supplied <template>: this class only finds the
//     template inside its root, clones it, fills [data-name] elements, and returns the clone.
//     All tooltip styling lives in CSS (company-dividends-chart.css).

class CompanyDividendsChart extends BarChartD3 {
    getDefaultOptions() {
        return {
            colors: { default: '#4CAF50', hover: '#388E3C' },
            valueFormat: ',.2f',
            axisTickFormat: ',.2f',
            showValueLabels: false,
            showLegend: false,
            showAxes: true,
            hideZeroTick: true,
            barPadding: 0.5,
            barCornerRadius: 1
            // No `margin` — rely on the base class default, which reserves space on
            // whichever side the Y axis lives on (right by default).
        };
    }

    // Tooltip rendering — clone the server-supplied <template>, fill [data-name] slots,
    // and let the base class position it. All visual styling is owned by CSS.
    //
    // Expected template structure (rendered by the server inside the chart root):
    //   <template class="barchart-tooltip-template">
    //     <div class="barchart-tooltip">
    //       <div class="barchart-tooltip-title" data-name="title"></div>
    //       <div class="barchart-tooltip-row">
    //         <span class="barchart-tooltip-swatch" data-name="swatch"></span>
    //         <span>Dividend: <span data-name="amount"></span></span>
    //       </div>
    //     </div>
    //   </template>
    renderTooltipContent(d) {
        const template = this._root.querySelector('template.barchart-tooltip-template');
        if (!template) return null;

        // template.content is a DocumentFragment — clone it so we keep the template intact.
        const fragment = template.content.cloneNode(true);
        // The fragment is expected to contain a single root element (.barchart-tooltip).
        const node = fragment.firstElementChild;
        if (!node) return null;

        const meta = d.meta || {};
        const title = meta.period || d.label;
        const amount = meta.formattedAmount || this._formatValue(d.value);

        const titleEl = node.querySelector('[data-name="title"]');
        if (titleEl) titleEl.textContent = title;

        const amountEl = node.querySelector('[data-name="amount"]');
        if (amountEl) amountEl.textContent = amount;

        const swatchEl = node.querySelector('[data-name="swatch"]');
        if (swatchEl) swatchEl.style.background = this.getBarColor(d, false);

        // Capture rows — fill from meta; hide the whole row when there is no value ("" or "—").
        this._fillCaptureRow(node, 'dropRatio', meta.formattedDropRatio);
        this._fillCaptureRow(node, 'gapPercent', meta.formattedGapPercent);
        this._fillCaptureRow(node, 'recovery', meta.formattedRecovery);

        return node;
    }

    _fillCaptureRow(node, name, value) {
        const valueEl = node.querySelector(`[data-name="${name}"]`);
        const rowEl = node.querySelector(`[data-name="${name}Row"]`);
        const hasValue = value != null && value !== '' && value !== '—';
        if (valueEl && hasValue) valueEl.textContent = value;
        if (rowEl && !hasValue) rowEl.style.display = 'none';
    }

    // Year mode: meta.group is undefined → isFirstInGroup is always true → label is rendered
    //            on every bar (label = "2024", shortLabel = "'24").
    // Month mode: server sets meta.group = year and meta.yearShort ("24"). We render the
    //             year label ("'24") only on the first month of each year; other months
    //             return null so the base class skips both the tick mark and the text.
    //             This also fixes the previously-observed bug where the last bar (when not
    //             first-in-group) received an unrelated "Apr"-style label via the isEdge
    //             override in _renderXAxis: returning null short-circuits that path
    //             through the `if (!label) continue` check.
    formatXAxisLabel(d, idx, total, slotWidth, strategy, isFirstInGroup) {
        const meta = d.meta || {};
        const yearShort = meta.yearShort;
        const isMonthMode = !!meta.monthLabel;

        if (isMonthMode) {
            // Only the first month of each year gets a label, regardless of strategy.
            // Same value for 'short' and 'full' so the base class's thinning measurements stay predictable.
            if (!isFirstInGroup) return null;
            return yearShort ? `'${yearShort}` : d.label;
        }

        // Year mode (or any subclass-supplied data without monthLabel) — preserve original behaviour.
        if (strategy === 'short') return d.shortLabel || d.label;
        return d.label;
    }
}

Tango.registerComponent(CompanyDividendsChart);